import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp, MaskedLines } from "@/components/Reveal";
import StarField from "@/components/StarField";

const FloorPanel = ({ index, name, tagline, copy, features, mainImg, mainAlt, smallImg, smallAlt, dark, cta }) => {
  const flipped = index === "02" || index === "04";
  return (
  <div className={`min-h-screen flex items-center py-24 sm:py-32 relative ${dark ? "text-ivory" : "text-ink"}`}>
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-14 items-center w-full relative z-10">
      <div className={`lg:col-span-5 ${flipped ? "lg:order-2 lg:col-start-8" : ""}`}>
        <FadeUp>
          <span className={`font-serif text-8xl sm:text-9xl leading-none block mb-6 ${dark ? "outline-number-dark" : "outline-number"}`}>
            {index}
          </span>
          <p className={`text-[11px] tracking-[0.45em] uppercase mb-4 ${dark ? "text-gold" : "text-coral"}`}>{tagline}</p>
          <h3 className="font-serif font-light text-4xl sm:text-5xl leading-tight mb-8">{name}</h3>
          <p className={`text-base sm:text-lg font-light leading-relaxed mb-10 ${dark ? "text-ivory/70" : "text-ink/65"}`}>{copy}</p>
          <ul className="space-y-3">
            {features.map((f) => (
              <li key={f} className={`flex items-center gap-3 text-sm tracking-wider ${dark ? "text-ivory/60" : "text-ink/55"}`}>
                <span className={`w-6 h-px ${dark ? "bg-gold" : "bg-coral"}`} />
                {f}
              </li>
            ))}
          </ul>
          {cta && (
            <a
              href={cta.href}
              data-testid={cta.testId}
              className="inline-flex items-center gap-3 mt-10 px-8 py-3.5 rounded-full bg-blush text-midnight text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-gold transition-colors duration-300"
            >
              {cta.label}
              <span aria-hidden="true">→</span>
            </a>
          )}
        </FadeUp>
      </div>
      <div className={`lg:col-span-7 relative ${flipped ? "lg:order-1" : ""}`}>
        <FadeUp delay={0.15}>
          <div className={`rounded-t-[999px] overflow-hidden aspect-[4/5] max-w-[520px] mx-auto ${dark ? "spotlight-frame-dark" : "spotlight-frame"}`}>
            <motion.img
              src={mainImg}
              alt={mainAlt}
              className="w-full h-full object-cover"
              loading="lazy"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
        </FadeUp>
        <FadeUp delay={0.35} className={`absolute -bottom-10 ${flipped ? "-right-2 sm:right-6" : "-left-2 sm:left-6"}`}>
          {smallImg && (
            <img
              src={smallImg}
              alt={smallAlt}
              loading="lazy"
              className={`w-36 h-36 sm:w-48 sm:h-48 rounded-full object-cover border-4 ${dark ? "border-midnight spotlight-frame-dark" : "border-ivory spotlight-frame"}`}
            />
          )}
        </FadeUp>
      </div>
    </div>
  </div>
  );
};

export default function Showcase() {
  const starRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: starRef, offset: ["start 95%", "start 15%"] });
  const bg = useTransform(scrollYProgress, [0, 1], ["#FDFBF7", "#0A0B1A"]);

  return (
    <motion.section id="floors" data-testid="showcase-section" style={{ backgroundColor: bg }} className="relative">
      <div className="pt-32 sm:pt-44 pb-8 text-center px-6">
        <FadeUp>
          <p className="text-[12px] tracking-[0.45em] uppercase text-coral mb-6">The Collection</p>
        </FadeUp>
        <h2 className="font-serif font-light text-ink leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
          <MaskedLines inView lines={[<>Five <span className="italic text-coral">Experiences</span></>]} />
        </h2>
      </div>

      <div data-testid="floor-card-white">
        <FloorPanel
          index="01"
          tagline="Seamless All-White Dancefloor"
          name={<>A flawless white <span className="italic text-coral">floor</span> you can dance on</>}
          copy="Our signature seamless all-white wrap turns any venue into a storybook ballroom. Laid as one continuous sheet, hand-smoothed to a flawless finish, and completed with your monogram inlaid in gold, blush or anything you dream."
          features={["Seamless white vinyl", "Hand-inlaid custom monograms", "Any size, any shape, any venue"]}
          mainImg="/assets/floors/after.webp"
          mainAlt="Seamless all-white dancefloor in an elegant ballroom"
        />
      </div>

      <div data-testid="floor-card-gold">
        <FloorPanel
          index="02"
          tagline="Mirror & Metallic"
          name={<>Liquid <span className="italic text-gold">gold</span> <span className="text-ink">or</span> <span className="italic text-[#A9B2BC]">silver</span> beneath your feet</>}
          copy="A floor that behaves like champagne. Our mirror and metallic finishes — liquid gold or gleaming silver — catch every candle, every sparkler, every flash, throwing light back into the room so the whole celebration glows."
          features={["True mirror gold & silver finishes", "Reflects candlelight beautifully", "Pairs with white script monograms"]}
          mainImg="/assets/floors/gold.webp"
          mainAlt="Mirror gold dancefloor reflecting warm ballroom light"
          smallImg="/assets/floors/mirror-silver.webp"
          smallAlt="Black and silver mirror dancefloor with geometric pattern under crystal chandeliers"
        />
      </div>

      <div data-testid="floor-card-printed">
        <FloorPanel
          index="03"
          tagline="Any Artwork · Any Color · Any Story"
          name={<>Custom Printed & <span className="italic text-coral">Designed</span> Dancefloors</>}
          copy="If you can dream it, we can print it. Bold painterly swirls, snowflakes for a winter fortieth, your monogram floating in a sea of color — designed with you, proofed to the pixel, and produced edge-to-edge so the entire floor becomes the artwork."
          features={["Full-color, edge-to-edge printed designs", "Monograms, logos & bespoke artwork", "Designed with you & proofed before print"]}
          mainImg="/assets/floors/printed-swirl.webp"
          mainAlt="Custom printed dancefloor with colorful painterly swirl artwork and black monogram"
          smallImg="/assets/floors/winter-wonderland.webp"
          smallAlt="Custom printed blue winter wonderland dancefloor with snowflakes"
        />
      </div>

      <div ref={starRef} data-testid="floor-card-starlit" className="relative">
        <motion.div style={{ opacity: scrollYProgress }} className="absolute inset-0">
          <StarField scroll={scrollYProgress} />
        </motion.div>
        <FloorPanel
          dark
          index="04"
          tagline="Starlit LED"
          name={<>Dance on the <span className="italic text-blush">night sky</span></>}
          copy="Hundreds of hand-wired LEDs twinkle beneath a sleek midnight surface. The lights dim, the floor ignites, and for one song the whole room forgets there is a ceiling at all."
          features={["Hundreds of individually tested LEDs", "Adjustable twinkle & brightness", "The floor guests never stop photographing"]}
          mainImg="/assets/floors/lakeview-led-white.jpg"
          mainAlt="White starlit LED dancefloor sparkling in a grand purple-lit ballroom"
          smallImg="/assets/floors/led-black.webp"
          smallAlt="Black starlit LED dancefloor twinkling in daylight"
        />
        <div data-testid="floor-card-infinity">
          <FloorPanel
            dark
            index="05"
            tagline="The Floor That Never Ends"
            name={<>Infinity! LED Mirror <span className="italic text-blush">Dancefloors</span></>}
            copy="Look down and fall in. Each mirrored tile holds a tunnel of light that plunges forever — a floor with no bottom, pulsing in any color you can dream, beat for beat with your music. The moment the first guest steps on, every phone in the room comes out."
            features={["Mirror-topped tiles with LEDs reflecting into forever", "Every color of the rainbow — synced to the music", "Guests literally dance on top of the light"]}
            mainImg="/assets/floors/infinity.webp"
            mainAlt="Infinity LED mirror dancefloor glowing in pink, purple and white"
          />
        </div>
      </div>
    </motion.section>
  );
}
